import { CdkListbox, CdkListboxModule } from '@angular/cdk/listbox';
import { CommonModule } from '@angular/common';
import { DestroyRef, type WritableSignal, input } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import FuzzySearch from 'fuzzy-search';
import { distinctUntilChanged } from 'rxjs';
import { PalantirAccordionComponent } from '../../../../accordion/accordion.component';
import { PalantirCheckboxComponent } from '../../../../checkbox/checkbox.component';
import { PalantirIconComponent } from '../../../../icon/icon.component';
import { PalantirInputComponent } from '../../../../input/input.component';
import { PalantirTextComponent } from '../../../../text/text.component';
import type { PalantirFilterModalInstance } from '../../../filter-modal.instance';
import { PalantirFilterMultiSelectInput, PalantirFilterOption } from '../../../filter-modal.type';
import { normalizeString, removeNormalizedFromOption } from '../../../filter-modal.utils';
import { PalantirFilterModalErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { PalantirFilterModalOptionComponent } from '../../shared/option/option.component';
import { filterModalInputLogger } from '../logger.instance';

const logger = filterModalInputLogger.extend('multi-select');

@Component({
  selector: 'palantir-filter-modal-multi-select-input-component',
  standalone: true,
  imports: [
    CommonModule,
    PalantirInputComponent,
    PalantirAccordionComponent,
    PalantirCheckboxComponent,
    CdkListboxModule,
    ReactiveFormsModule,
    PalantirIconComponent,
    PalantirTextComponent,
    PalantirFilterModalErrorDisplayComponent,
    PalantirFilterModalOptionComponent,
  ],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirFilterModalMultiSelectInputComponent {
  @ViewChild('listBox', { static: true, read: CdkListbox })
  public listBox!: CdkListbox;

  public readonly definition = input.required<PalantirFilterMultiSelectInput>();

  public readonly control = input.required<FormControl>();

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public readonly modalInstance = input.required<PalantirFilterModalInstance<{}>>();

  // eslint-disable-next-line unicorn/consistent-function-scoping
  public canShowFilter = computed(() => {
    const canShow = this.modalInstance().dependsOnFilterComputed(this.definition().dependsOn);
    logger.debug(`Visibility check for "${this.definition().label}":`, canShow);
    return canShow;
  });

  public searchControl: FormControl = new FormControl<string>('');

  private searchEngine!: FuzzySearch<PalantirFilterOption>;

  public filteredOptions: WritableSignal<PalantirFilterOption[]> = signal([]);

  public constructor(private readonly destroyRef: DestroyRef) {}

  public ngAfterViewInit(): void {
    const definition = this.definition();
    this.filteredOptions.set(definition.options);

    const optionsWithNormalizedValues = structuredClone(
      definition.options.map((option) => ({
        ...option,
        normalized: normalizeString(option.label),
      })),
    );

    this.searchEngine = new FuzzySearch(
      optionsWithNormalizedValues,
      ['normalized', 'label', 'value'],
      {
        caseSensitive: false,
        sort: true,
      },
    );

    this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleSearchChange(value));

    // Race condition between the control value and the list box options in html
    const control = this.control();
    if (control.value.length) {
      setTimeout(() => {
        this.listBox.writeValue(this.control().value);
      }, 100);
    }

    control.valueChanges.subscribe((value) => {
      this.listBox.writeValue(value);
    });
  }

  private handleSearchChange(value: string): void {
    logger.debug(`Search query changed for "${this.definition().label}":`, value);
    if (!value) {
      this.filteredOptions.set(this.definition().options);
      return;
    }

    const results = this.searchEngine.search(value).map(removeNormalizedFromOption);
    logger.debug(`Search results for "${this.definition().label}":`, results.length);
    this.filteredOptions.set(results);
  }

  public handleCheckboxChange(value: readonly unknown[]): void {
    this.control()?.setValue(value);
    this.control()?.markAsDirty();
  }

  /**
   * Previne a propagação do eventos para não interagir com a checkbox
   */
  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Alterna a seleção de uma opção no controle de formulário.
   *
   * @param control - O controle de formulário que mantém o array de valores selecionados.
   * @param option - A opção a ser alternada.
   */
  public toggle(control: FormControl, option: PalantirFilterOption): void {
    const currentValue = control.value as unknown[];
    const optionIndex = currentValue.indexOf(option.value);
    const isSelected = optionIndex !== -1;
    const newValue = isSelected
      ? currentValue.filter((_, index) => index !== optionIndex)
      : [...currentValue, option.value];
    control.setValue(newValue);
    control.markAsDirty();
  }
}
